import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import { SUPPLIERS_KEY } from "./hooks";
import { useAuth, hasRole } from "../auth/AuthProvider";
import type {
  AssignManualRequest,
  AwardDecisionRequest,
  AwardOfferRequest,
  ItemBoardAttentionEntry,
  ItemBoardEntry,
  ItemOffer,
  ModerateRegistrationRequest,
  ModerationStatus,
  PendingAwardApproval,
  SupplierRegistration,
} from "../types";

export const ITEM_BOARD_KEY = ["item-board"] as const;
export const ITEM_BOARD_ATTENTION_KEY = ["item-board", "attention"] as const;
export const ITEM_OFFERS_KEY = ["item-offers"] as const;
export const SUPPLIER_REGISTRATIONS_KEY = ["supplier-registrations"] as const;
export const AWARD_APPROVALS_KEY = ["award-approvals"] as const;

function useInvalidateBoard(processId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: [...ITEM_BOARD_KEY, processId] });
    qc.invalidateQueries({ queryKey: ["process", processId] });
  };
}

// ---- доска позиций ----
export function useItemBoard(processId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: [...ITEM_BOARD_KEY, processId],
    queryFn: () => api.get<ItemBoardEntry[]>(`/processes/${processId}/item-board`),
    enabled: enabled && !!processId,
    refetchInterval: 10_000,
  });
}

export function usePublishItem(processId: string) {
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ detail: string }>(`/items/${itemId}/publish`),
    onSuccess: invalidate,
  });
}

export function useUnpublishItem(processId: string) {
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ detail: string }>(`/items/${itemId}/unpublish`),
    onSuccess: invalidate,
  });
}

export function useReopenItem(processId: string) {
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ detail: string }>(`/items/${itemId}/reopen`),
    onSuccess: invalidate,
  });
}

export function useCancelBoardItem(processId: string) {
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ detail: string }>(`/items/${itemId}/cancel`),
    onSuccess: invalidate,
  });
}

export function useDeliverItem(processId: string) {
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: (itemId: string) =>
      api.post<{ detail: string }>(`/items/${itemId}/delivered`),
    onSuccess: invalidate,
  });
}

export function useSetItemDeadline(processId: string) {
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: ({ itemId, delivery_deadline }: { itemId: string; delivery_deadline: string }) =>
      api.patch<{ detail: string }>(`/items/${itemId}/deadline`, { delivery_deadline }),
    onSuccess: invalidate,
  });
}

export function useAutotagItems(processId: string) {
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: () =>
      api.post<{ detail: string }>(`/processes/${processId}/items/autotag`),
    onSuccess: invalidate,
  });
}

export function usePublishAllItems(processId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: () =>
      api.post<{ detail: string }>(`/processes/${processId}/items/publish-all`),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ITEM_BOARD_ATTENTION_KEY });
    },
  });
}

// ---- сводная доска «требуют внимания» по всем заявкам ----
export function useItemBoardAttention(enabled = true) {
  return useQuery({
    queryKey: ITEM_BOARD_ATTENTION_KEY,
    queryFn: () => api.get<ItemBoardAttentionEntry[]>("/item-board/attention"),
    enabled,
    refetchInterval: 15_000,
  });
}

// ---- КП по позиции ----
export function useItemOffers(itemId: string | null) {
  return useQuery({
    queryKey: [...ITEM_OFFERS_KEY, itemId],
    queryFn: () => api.get<ItemOffer[]>(`/items/${itemId}/offers`),
    enabled: !!itemId,
  });
}

export function useAwardOffer(processId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: AwardOfferRequest }) =>
      api.post<{ detail: string }>(`/items/${itemId}/award`, payload),
    onSuccess: (_data, vars) => {
      invalidate();
      qc.invalidateQueries({ queryKey: [...ITEM_OFFERS_KEY, vars.itemId] });
      qc.invalidateQueries({ queryKey: AWARD_APPROVALS_KEY });
    },
  });
}

export function useAssignManual(processId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateBoard(processId);
  return useMutation({
    mutationFn: ({ itemId, payload }: { itemId: string; payload: AssignManualRequest }) =>
      api.post<{ detail: string }>(`/items/${itemId}/assign-manual`, payload),
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: AWARD_APPROVALS_KEY });
    },
  });
}

// ---- модерация регистраций ----
export function useSupplierRegistrations(status: ModerationStatus = "pending") {
  return useQuery({
    queryKey: [...SUPPLIER_REGISTRATIONS_KEY, status],
    queryFn: () =>
      api.get<SupplierRegistration[]>(
        `/suppliers/registrations?moderation_status=${status}`,
      ),
  });
}

export function useModerateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, payload }: { supplierId: string; payload: ModerateRegistrationRequest }) =>
      api.post<{ detail: string }>(`/suppliers/${supplierId}/moderate`, payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: SUPPLIER_REGISTRATIONS_KEY });
      qc.invalidateQueries({ queryKey: SUPPLIERS_KEY });
      qc.invalidateQueries({ queryKey: ["supplier", vars.supplierId] });
    },
  });
}

// ---- согласование победителей ----
export function usePendingAwardApprovals(enabled = true) {
  // Очередь победителей сервер отдаёт согласующему, руководителю отдела и
  // администратору (AnyApproverDep). Закупщику и аналитику — 403, и опрос
  // раз в 10 секунд с повторами засорял консоль ошибками.
  const { user } = useAuth();
  const allowed = hasRole(user, "approver", "operator", "admin");
  return useQuery({
    queryKey: AWARD_APPROVALS_KEY,
    queryFn: () => api.get<PendingAwardApproval[]>("/award-approvals/pending"),
    enabled: enabled && allowed,
    refetchInterval: 10_000,
  });
}

export function useAwardApprovalDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ awardId, payload }: { awardId: string; payload: AwardDecisionRequest }) =>
      api.post<{ detail: string }>(`/award-approvals/${awardId}/decision`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: AWARD_APPROVALS_KEY });
      qc.invalidateQueries({ queryKey: ITEM_BOARD_KEY });
    },
  });
}
